import {
  selectHasLoadedTrainings,
  selectProjects,
} from "../../../app/slices/ProjectsSlice";
import { OnboardingTarget } from "../onboardingTargets";
import type {
  OnboardingStepDefinition,
  OnboardingTour,
} from "../onboardingTypes";

/**
 * The dashboard orientation tour, in two stages.
 *
 * Content: geography (the list), how to act on what is in it, the action that
 * unblocks everything (new training), and the one concept that is not visible
 * from the screen (the asset library is organisation wide, not per training).
 *
 * The previous system had five steps, but they were not five facts. Two were
 * dropped rather than ported: the sidebar Dashboard button, which navigates to
 * the page the tour is running on, and the sidebar New Training item, which
 * duplicates the header CTA. See docs/DASHBOARD_ONBOARDING_PLAN.md 2.
 *
 * The open_training step is new to both tours. Opening a training is the most
 * used action on this screen and it has no affordance at all: the row is
 * clickable, with a hover tint and nothing else. It says nothing about the row
 * menu, deliberately, because that menu's other entry is Archive and a first
 * run tour should not be teaching people how to remove their colleagues' work.
 *
 * WHY TWO STAGES. That step anchors the rows and tells the author to click one.
 * An organisation with no trainings has no rows, so the step would spotlight an
 * empty table body and describe something that is not there. Stage A drops it;
 * stage B, for an organisation that has trainings, is the full four.
 *
 * Both stages wait on `hasLoadedTrainings` rather than reading the row count
 * alone. `projects` starts as an empty array, so a count on its own cannot tell
 * an empty organisation from one whose first fetch is still in flight, and the
 * loser of that race gets the wrong tour recorded as completed. This is the
 * same shape of bug as the NotSet strategy trap in section 4 of the plan.
 *
 * Step 1 anchors the whole list and asks for "top-start" with a zero offset.
 * That combination is what the previous onboarding used, and it is worth
 * keeping. The list is roughly 490px tall on every screen, because the page
 * size is fixed at five rows (ProjectsSlice.tsx), so on a 960px window there is
 * no room for a 316px panel above it (165px) or below it (292px). Asking for
 * the top edge with no gap means popper seats the panel flush under the list
 * where there is room, and slides it up over the list where there is not,
 * instead of pushing it off the bottom of the window.
 *
 * Verified in Chromium across three list heights and five window heights: the
 * panel is fully on screen in all fifteen. Two things make that work and both
 * must stay: the zero offset, and the viewport boundaries TourRunner passes to
 * popper, without which the clamp measures the document instead of the window.
 * See docs/DASHBOARD_ONBOARDING_PLAN.md 9.4.
 */

const K = "onboarding.tour.dashboard";

const step = (
  target: OnboardingStepDefinition["target"],
  name: string,
  placement: OnboardingStepDefinition["placement"],
  offset?: number,
): OnboardingStepDefinition => ({
  target,
  titleKey: `${K}.${name}.title`,
  bodyKey: `${K}.${name}.body`,
  placement,
  ...(offset !== undefined ? { offset } : {}),
});

/** Waits for a response: see WHY TWO STAGES above. */
const trainingCount = (state: any) =>
  selectHasLoadedTrainings(state) ? (selectProjects(state) ?? []).length : null;
const completedStageA = (state: any) =>
  state?.onboarding?.completedTours?.includes("dashboardStageA") ?? false;

/** Anchored on regions that exist whatever the row count. */
const listStep = step(
  OnboardingTarget.DashboardProjectsTable,
  "projects",
  "top-start",
  0,
);

const closingSteps: OnboardingStepDefinition[] = [
  step(OnboardingTarget.DashboardNewTraining, "new_training", "bottom"),
  step(OnboardingTarget.DashboardFileExplorer, "library", "right"),
];

const emptyOrganisationSteps: OnboardingStepDefinition[] = [
  listStep,
  ...closingSteps,
];

/** Needs a row to point at, so it is absent from stage A entirely. */
const openTrainingStep = step(
  OnboardingTarget.DashboardTrainingRows,
  "open_training",
  "top-start",
  0,
);

/** Empty organisation: nothing to open yet, so go straight to creating one. */
export const dashboardStageA: OnboardingTour = {
  id: "dashboardStageA",
  canRun: (state) => trainingCount(state) === 0,
  steps: emptyOrganisationSteps,
  progressSegment: () => ({
    startIndex: 0,
    labelKey: `${K}.progress.overview`,
  }),
};

/**
 * Has trainings: the whole tour.
 *
 * An author who lands here having already seen stage A (they had no trainings,
 * took the tour, then made one) skips the steps stage A already covered and
 * gets only the row step, which is the single thing stage A could not show.
 */
export const dashboardStageB: OnboardingTour = {
  id: "dashboardStageB",
  canRun: (state) => (trainingCount(state) ?? 0) > 0,
  // Keep the shared stage-A steps together. A returning author can then start
  // after that block and see only the newly available row step.
  steps: [...emptyOrganisationSteps, openTrainingStep],
  initialStepIndex: (state) =>
    completedStageA(state) ? emptyOrganisationSteps.length : 0,
  progressSegment: (state) => ({
    startIndex: completedStageA(state) ? emptyOrganisationSteps.length : 0,
    labelKey: `${K}.progress.overview`,
  }),
};

export const dashboardTours: OnboardingTour[] = [
  dashboardStageA,
  dashboardStageB,
];
